import { animate, MotionValue, useMotionValue, useReducedMotion, useSpring, useTransform } from 'framer-motion';
import { RefCallback, useCallback, useEffect, useMemo, useRef } from 'react';

import { BIZY_EYE_CENTER, BIZY_VIEWBOX, bizyEyeTrackingManager, type BizyEyeGeometry } from './bizy-eye-tracking';
import { useReduceBizyMotionPreference } from './bizy-motion-preferences';

const EYE_TRACK_SPRING = {
	stiffness: 260,
	damping: 22,
	mass: 0.35,
};

const PEEK_SWAY_X = 2.5;
const PEEK_SWAY_DURATION_S = 0.9;

const useFinePointer = (): boolean => {
	return useMemo(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return false;
		}

		return window.matchMedia('(pointer: fine)').matches;
	}, []);
};

type UseBizyEyeTrackingOptions = {
	enabled: boolean;
	/** Idle left-right eye sway when the pointer is not nearby (peeking pose). */
	peekIdleSway?: boolean;
	eyeCenterX?: number;
	eyeCenterY?: number;
	viewBox?: BizyEyeGeometry['viewBox'];
};

type UseBizyEyeTrackingResult = {
	svgRef: RefCallback<SVGSVGElement>;
	eyeOffsetX: MotionValue<number>;
	eyeOffsetY: MotionValue<number>;
};

export const useBizyEyeTracking = ({
	enabled,
	peekIdleSway = false,
	eyeCenterX = BIZY_EYE_CENTER.x,
	eyeCenterY = BIZY_EYE_CENTER.y,
	viewBox = BIZY_VIEWBOX,
}: UseBizyEyeTrackingOptions): UseBizyEyeTrackingResult => {
	const svgElementRef = useRef<SVGSVGElement | null>(null);
	const subscriberRef = useRef<Parameters<typeof bizyEyeTrackingManager.register>[0] | null>(null);
	const geometryRef = useRef<BizyEyeGeometry>({
		center: { x: eyeCenterX, y: eyeCenterY },
		viewBox,
	});
	geometryRef.current = {
		center: { x: eyeCenterX, y: eyeCenterY },
		viewBox,
	};
	const offsetX = useMotionValue(0);
	const offsetY = useMotionValue(0);
	const pointerNearby = useMotionValue(false);
	const swayX = useMotionValue(0);
	const springX = useSpring(offsetX, EYE_TRACK_SPRING);
	const springY = useSpring(offsetY, EYE_TRACK_SPRING);
	const swaySpringX = useSpring(swayX, EYE_TRACK_SPRING);
	const shouldReduceMotion = useReducedMotion();
	const [reduceBizyMotion] = useReduceBizyMotionPreference();
	const hasFinePointer = useFinePointer();
	const swayLoopRef = useRef<{ cancelled: boolean } | null>(null);
	const wasPointerNearbyRef = useRef(false);

	const isActive = (enabled || peekIdleSway) && hasFinePointer && !shouldReduceMotion && !reduceBizyMotion;

	const stopSwayLoop = useCallback(() => {
		if (swayLoopRef.current) {
			swayLoopRef.current.cancelled = true;
			swayLoopRef.current = null;
		}
		swayX.stop();
		swayX.set(0);
	}, [swayX]);

	const startSwayLoop = useCallback(() => {
		stopSwayLoop();

		const loopState = { cancelled: false };
		swayLoopRef.current = loopState;

		const run = async () => {
			while (!loopState.cancelled) {
				await animate(swayX, -PEEK_SWAY_X, { duration: PEEK_SWAY_DURATION_S, ease: 'easeInOut' });
				if (loopState.cancelled) {
					break;
				}
				await animate(swayX, PEEK_SWAY_X, { duration: PEEK_SWAY_DURATION_S, ease: 'easeInOut' });
			}
		};

		void run();
	}, [stopSwayLoop, swayX]);

	const unregister = useCallback(() => {
		if (subscriberRef.current) {
			bizyEyeTrackingManager.unregister(subscriberRef.current);
			subscriberRef.current = null;
		}
	}, []);

	const syncRegistration = useCallback(() => {
		unregister();
		stopSwayLoop();

		if (!isActive) {
			offsetX.set(0);
			offsetY.set(0);
			pointerNearby.set(false);
			return;
		}

		const element = svgElementRef.current;
		if (!element) {
			offsetX.set(0);
			offsetY.set(0);
			pointerNearby.set(false);
			return;
		}

		const subscriber = {
			element,
			getGeometry: () => geometryRef.current,
			onUpdate: ({ x, y, pointerNearby: nearby }: { x: number; y: number; pointerNearby: boolean }) => {
				offsetX.set(x);
				offsetY.set(y);
				pointerNearby.set(nearby);

				if (!peekIdleSway) {
					return;
				}

				if (nearby !== wasPointerNearbyRef.current) {
					wasPointerNearbyRef.current = nearby;
					if (nearby) {
						stopSwayLoop();
					} else {
						startSwayLoop();
					}
				}
			},
		};

		bizyEyeTrackingManager.register(subscriber);
		subscriberRef.current = subscriber;
		wasPointerNearbyRef.current = false;

		if (peekIdleSway) {
			startSwayLoop();
		}
	}, [isActive, offsetX, offsetY, peekIdleSway, pointerNearby, startSwayLoop, stopSwayLoop, unregister]);

	const svgRef = useCallback(
		(node: SVGSVGElement | null) => {
			svgElementRef.current = node;
			syncRegistration();
		},
		[syncRegistration]
	);

	useEffect(() => {
		syncRegistration();

		return () => {
			stopSwayLoop();
			unregister();
		};
	}, [syncRegistration, stopSwayLoop, unregister]);

	const eyeOffsetX = useTransform(() => {
		if (peekIdleSway && !pointerNearby.get()) {
			return swaySpringX.get();
		}
		return springX.get();
	});
	const eyeOffsetY = useTransform(() => {
		if (peekIdleSway && !pointerNearby.get()) {
			return 0;
		}
		return springY.get();
	});

	return {
		svgRef,
		eyeOffsetX,
		eyeOffsetY,
	};
};
